import React, { Component, Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Drawer, Popover, Table } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import H3Text from '@Uilib/h3Text';
import { QUANTITY, toISTDate } from '@Apis/constants';
import Helpers from '@Apis/helpers';
import H3Image from '@Uilib/h3Image';
import ProductActions from '@Actions/productActions';
import closeIcon from '@Images/icons/icon-close-blue.png';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import OtherDepartmentsStock from '@Components/Inventory/ManageProducts/ProductsList/OtherDepartmentsStock';
import RecentTransactions from '@Components/Inventory/ManageProducts/ViewProduct/RecentTransactions';
import ProductCategoryLabel from '../../../../Common/ProductCategoryLabel';
import HideValue from '../../../../Common/RestrictedAccess/HideValue';
import PRZModal from '../../../../Common/UI/PRZModal';
import './style.scss';
import dayjs from 'dayjs';

function PurchaseIndentLines ({
  piLines,
  selectedPurchaseIndent,
  visibleColumns,
  cfPurchaseIndentLine,
  priceMasking,
  user,
}) {

  const [state, setState] = useState({
    showPRZModal: false,
    selectedDocumentId: null,
  });
  const {
    showPRZModal,
    selectedDocumentId,
  } = state;
  const updateState = (object) => {
    setState({
      ...state,
      ...object,
    });
  };

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  const getColumns = () => {

    const columns = [
      {
        title: '',
        responsive: ['xs'],
        render: (record) => (
          <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
            <div className="mobile-list__item">
              <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
                <H3Text text={`SKU Number: ${record?.product_sku_info?.internal_sku_code}`} className="mobile-list__item-number" />
              </Link>
              <H3Text text={record?.product_sku_name} className="mobile-list__item-info" />
              <H3Text text={`Quantity: ${QUANTITY(record?.quantity, record?.uom_info?.[0]?.precision)} ${record?.uom_info?.[0]?.qc?.toProperCase()}`} className="mobile-list__item-info" />
              <H3Text text={`Required By Date: ${toISTDate(record?.required_by_date, 'DD/MM/YYYY')}`} className="mobile-list__item-info" />
              <H3Text text={`Planning Number: ${record?.planning_number}`} className="mobile-list__item-info" />
            </div>
          </Link>
        ),
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: 'Product',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => {
          return (
            <Fragment>
              <div onClick={() => updateState({ showPRZModal: true, selectedDocumentId: record?.product_sku_info?.product_sku_id })}>
                <a>
                  {`#${record?.product_sku_info?.internal_sku_code}`}
                </a>
              </div>
              <div className="flex-display" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ maxWidth: '200px' }}>
                  {`${record.product_sku_info?.ref_product_code ? `${record.product_sku_info?.ref_product_code} - ` : ''}${record.product_sku_name}`?.trim()}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: (isDataMaskingPolicyEnable && isHideCostPrice) ? '' : record?.remarks,
                    }}
                    className="table-subscript"
                    style={{
                      width: '200px',
                      whiteSpace: 'pre-wrap',
                    }}
                  />
                  {(isDataMaskingPolicyEnable && isHideCostPrice) && <HideValue showPopOver />}
                  {!record?.tenant_product_id && <H3Text text="New Product" className="view-pi-selection__new-product" />}
                </div>
                <div className="flex-align-center-justify-between" style={{ gap: '10px' }}>
                  {record?.product_sku_info?.product_category_info?.category_path?.length > 0 && (
                    <ProductCategoryLabel
                      categoryPath={record?.product_sku_info?.product_category_info?.category_path}
                      categoryName={record?.product_sku_info?.product_category_info?.category_path?.at(
                        -1,
                      )}
                      containerStyle={{
                        width: 'fit-content',
                      }}
                    />
                  )}
                  {Helpers.getPermission(Helpers.permissionEntities.PURCHASE_INDENT, Helpers.permissionTypes.CREATE, user) && (
                    <RecentTransactions
                      tenantId={selectedPurchaseIndent?.tenant_id}
                      productSkuId={record?.product_sku_info?.product_sku_id}
                      internalSkuCode={record?.product_sku_info?.internal_sku_code}
                      refProductCode={record?.product_sku_info?.ref_product_code}
                      productSkuName={record?.product_sku_name}
                    />
                  )}
                </div>
              </div>
            </Fragment>
          );
        },
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: 'Quantity',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => `${QUANTITY(item?.quantity, item?.uom_info?.[0]?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()}`,
        visible: visibleColumns?.QUANTITY?.visible,
      },
      {
        title: 'Required By Date',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => (item?.required_by_date ? dayjs(item?.required_by_date)?.format('DD/MM/YYYY') : '-'),
        visible: visibleColumns?.REQ_BY_DATE?.visible,
      },
      {
        title: 'Planning Number',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => (item?.planning_number),
        visible: visibleColumns?.PLAN_NUMBER?.visible,
      },
    ];
    if (cfPurchaseIndentLine?.length) {
      columns.splice(4, 0, ...CustomFieldHelpers.renderCustomLineColumns(false, cfPurchaseIndentLine, visibleColumns, null));
    };

    return columns;
  }

  const getData = (data) => (JSON.parse(JSON.stringify(data)));

  return (
    <Fragment>
      <div className='pi-table-wrapper'>
        <Table
          bordered
          showHeader
          title={() => (`Purchase Indent Lines ${piLines?.length}`)}
          size='small'
          columns={getColumns()}
          dataSource={getData(piLines || [])}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </div>
      <PRZModal
        isOpen={showPRZModal}
        onClose={() => {
          updateState({ showPRZModal: false, selectedDocumentId: null });
        }}
        entityType="product"
        documentId={selectedDocumentId}
      />
    </Fragment>
  );
};

const mapStateToProps = ({ UserReducers, ProductReducers }) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  selectedOrgProduct: ProductReducers.selectedOrgProduct,
  getOrgProductByIdLoading: ProductReducers.getOrgProductByIdLoading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  getOrgProductById: (orgId, tenantId, productSkuId, callback) => dispatch(ProductActions.getOrgProductById(orgId, tenantId, productSkuId, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PurchaseIndentLines));